const DEFAULT_ENVIRONMENT = "dev";

function readEnv(name, fallback = "") {
  if (typeof process === "undefined" || !process.env) {
    return fallback;
  }

  const value = process.env[name];
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  return value;
}

function parseBoolean(value, fallback) {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  return String(value).toLowerCase() === "true";
}

function normalizeEnvironment(value) {
  const normalized = String(value ?? DEFAULT_ENVIRONMENT).trim().toLowerCase();
  if (normalized === "production") {
    return "prod";
  }
  if (["dev", "uat", "prod"].includes(normalized)) {
    return normalized;
  }
  return DEFAULT_ENVIRONMENT;
}

function normalizeTransport(value) {
  const defaultTransport = readEnv("NODE_ENV") === "test" ? "console" : "both";
  const source =
    value === undefined || value === null || String(value).trim() === ""
      ? defaultTransport
      : value;
  const normalized = String(source).trim().toLowerCase();
  if (["console", "http", "both", "none"].includes(normalized)) {
    return normalized;
  }
  return "both";
}

export function getTelemetryConfig() {
  const environment = normalizeEnvironment(readEnv("REACT_APP_TELEMETRY_ENV"));
  const enabled = parseBoolean(readEnv("REACT_APP_TELEMETRY_ENABLED"), true);
  const transport = normalizeTransport(readEnv("REACT_APP_TELEMETRY_TRANSPORT"));
  const sampleRate = Number(readEnv("REACT_APP_TELEMETRY_SAMPLE_RATE", "1"));
  const applicationInsightsConnectionString = readEnv(
    "REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING",
  ).trim();
  const applicationInsightsEnabled = parseBoolean(
    readEnv("REACT_APP_APPLICATIONINSIGHTS_ENABLED"),
    Boolean(applicationInsightsConnectionString),
  );

  return {
    enabled,
    environment,
    transport,
    sampleRate: Number.isFinite(sampleRate)
      ? Math.min(Math.max(sampleRate, 0), 1)
      : 1,
    httpEndpoint: readEnv("REACT_APP_TELEMETRY_HTTP_ENDPOINT", "/telemetry/events").trim(),
    moduleId: "tema",
    defaultRole: "teacher",
    defaultTenantId: "default",
    dedupeWindowMs: Number(readEnv("REACT_APP_TELEMETRY_DEDUPE_MS", "1500")),
    applicationInsightsConnectionString,
    applicationInsightsEnabled,
  };
}

export function isTelemetryEnabled() {
  const config = getTelemetryConfig();
  if (!config.enabled) {
    return false;
  }

  if (config.sampleRate >= 1) {
    return true;
  }

  if (config.sampleRate <= 0) {
    return false;
  }

  return Math.random() <= config.sampleRate;
}

export function shouldUseConsoleTransport() {
  const { transport } = getTelemetryConfig();
  return transport === "console" || transport === "both";
}

export function shouldUseHttpTransport() {
  const { transport } = getTelemetryConfig();
  return transport === "http" || transport === "both";
}

export function shouldUseAppInsightsTransport() {
  const config = getTelemetryConfig();
  return (
    config.applicationInsightsEnabled && Boolean(config.applicationInsightsConnectionString)
  );
}
